import { useState, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  CloseButton,
  Container,
  VStack,
  HStack,
  Stack,
  Box,
  Heading,
  Text,
  Button,
  Badge,
  Spinner,
  Alert,
  SimpleGrid,
  Input,
  useDisclosure,
  Icon,
  Dialog,
  Portal,
  Field,
  Checkbox,
  IconButton,
  Menu,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import {
  FiNavigation,
  FiDownload,
  FiTrash2,
  FiCheck,
  FiClock,
  FiUsers,
  FiMapPin,
  FiMoreVertical,
  FiArrowDown,
} from 'react-icons/fi'
import { TurfMap } from '../components/turf'
import { ResponsiveTable, ResponsiveModal, BackLink, type ResponsiveColumn } from '../components/shared'
import { PlacementControl } from '../components/turf/PlacementControl'
import { TurfMemberReorderList } from '../components/turf/TurfMemberReorderList'
import { buildStopFeatures, buildFallbackPath, buildGhostFeatures } from '../components/turf/turfMapLayers'
import { FindNearbyMenu } from '../components/turf/opportunistic/FindNearbyMenu'
import { GhostCard, type GhostCardDone } from '../components/turf/opportunistic/GhostCard'
import { pruneGhostsAfterAdd } from '../components/turf/opportunistic/ghostUtils'
import { UnionOnly } from '../components/scoping/UnionOnly'
import { useReturnTo } from '../lib/returnNav'
import { extractApiMessage } from '../lib/apiError'
import { useIsUnionTier } from '../hooks/useIsUnionTier'
import { useIsMobile } from '../hooks/useIsMobile'
import { useTurfDetail, useDeleteTurf, useOptimizeWalkOrder, useExportTurfToList, useSetPlacements, useMoveTurfMember, useRemoveTurfMember, useFindCandidates } from '../hooks/useTurf'
import type { CandidateSearchRequest, CandidateSearchResponse, TurfDetailDto, TurfMemberDto } from '../types/turf'
import type { ContactMarker, TurfBoundary } from '../types/turfMap'
import type * as GeoJSON from 'geojson'

function parseGeoJson<T>(json: string | null | undefined): T | null {
  if (!json) return null
  try {
    return JSON.parse(json) as T
  } catch {
    return null
  }
}

function turfToBoundaries(turf: TurfDetailDto): TurfBoundary[] {
  const geometry = parseGeoJson<GeoJSON.Polygon | GeoJSON.MultiPolygon>(turf.boundaryGeoJson)
  if (!geometry) return []
  return [{
    id: turf.id,
    label: turf.label,
    geoJson: { type: 'Feature', geometry, properties: {} } as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>,
  }]
}

function membersToMarkers(turf: TurfDetailDto): ContactMarker[] {
  return turf.members
    .filter(m => m.latitude != null && m.longitude != null)
    .map(m => ({
      id: m.contactId,
      longitude: m.longitude!,
      latitude: m.latitude!,
      label: m.contactName,
      walkOrder: m.walkOrder,
      isVisited: m.isVisited,
    }))
}

function toRouteGeoJson(turf: TurfDetailDto): GeoJSON.Feature<GeoJSON.LineString> | null {
  return parseGeoJson<GeoJSON.Feature<GeoJSON.LineString>>(turf.routeGeometryGeoJson)
}

/** Coming Monday 10:00 local, as 'YYYY-MM-DDTHH:mm' (datetime-local format). */
function nextMondayAt10(): string {
  const d = new Date()
  d.setDate(d.getDate() + (((8 - d.getDay()) % 7) || 7))
  d.setHours(10, 0, 0, 0)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function TurfDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const back = useReturnTo({ to: '/dashboard/turfs', label: 'Back to Turfs' })
  const cancelRef = useRef<HTMLButtonElement>(null)

  const [useSchedule, setUseSchedule] = useState(false)
  const [scheduleAt, setScheduleAt] = useState(() => nextMondayAt10())
  const [staged, setStaged] = useState<Record<string, 'Home' | 'Work'>>({})
  const { data: turf, isLoading, error } = useTurfDetail(id, useSchedule ? scheduleAt : undefined)
  const isUnion = useIsUnionTier()
  const isMobile = useIsMobile()
  const setPlacements = useSetPlacements(id)
  const moveMutation = useMoveTurfMember(id)
  const removeMutation = useRemoveTurfMember(id)
  const deleteMutation = useDeleteTurf()
  const optimizeMutation = useOptimizeWalkOrder()
  const exportMutation = useExportTurfToList()

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showOptimizeConfirm, setShowOptimizeConfirm] = useState(false)
  const [reorderMode, setReorderMode] = useState(false)
  const [moveTarget, setMoveTarget] = useState<TurfMemberDto | null>(null)
  const [movePosition, setMovePosition] = useState('')
  const [removeTarget, setRemoveTarget] = useState<TurfMemberDto | null>(null)
  const exportModal = useDisclosure()
  const [exportLabel, setExportLabel] = useState('')
  const [exportDescription, setExportDescription] = useState('')

  // Memoize map data to avoid re-renders
  const boundaries = useMemo(() => turf ? turfToBoundaries(turf) : [], [turf])
  const contacts = useMemo(() => turf ? membersToMarkers(turf) : [], [turf])
  const routeGeoJson = useMemo(() => turf ? toRouteGeoJson(turf) : null, [turf])

  // [O4] Union tier + a campaign turf: numbered stops (add-ins as diamonds) and a drive path
  // (the stored route, else a straight line through the stops). Campaign tier renders as before.
  const [showPath, setShowPath] = useState(true)
  const showStops = isUnion && !!turf?.isCampaignTurf
  const stops = useMemo(() => showStops && turf ? buildStopFeatures(turf.members) : undefined, [showStops, turf])
  const pathGeoJson = useMemo(
    () => routeGeoJson ?? (stops ? buildFallbackPath(stops) : null),
    [routeGeoJson, stops],
  )

  // [O4] "Find nearby stops": the search result lives in page state (ghosts on the map); a ghost or
  // diamond click opens its card, anchored at the clicked point on desktop.
  const { mutate: findCandidates, isPending: searching, error: searchError } = useFindCandidates(id)
  const [candidates, setCandidates] = useState<(CandidateSearchResponse & { shown: number }) | null>(null)
  const [card, setCard] = useState<{ ghostKey?: string; stopId?: string; anchor?: { x: number; y: number } } | null>(null)
  const ghostFeatures = useMemo(
    () => showStops && candidates ? buildGhostFeatures(candidates.ghosts) : undefined,
    [showStops, candidates],
  )
  const cardGhost = card?.ghostKey ? candidates?.ghosts.find(g => g.key === card.ghostKey) : undefined
  const cardStop = card?.stopId ? turf?.opportunisticStops?.find(s => s.id === card.stopId) : undefined

  const runSearch = (body: CandidateSearchRequest) => {
    setCard(null)
    findCandidates(body, { onSuccess: (res) => setCandidates({ ...res, shown: res.ghosts.length }) })
  }

  const closeCard = (result?: GhostCardDone) => {
    const key = card?.ghostKey
    if (result && key) {
      setCandidates(c => c && { ...c, ghosts: pruneGhostsAfterAdd(c.ghosts, key, result.addedContactIds) })
    }
    setCard(null)
  }

  const runOptimize = async () => {
    if (!id) return
    try {
      await optimizeMutation.mutateAsync(id)
      toaster.create({
        title: 'Walk order optimized',
        description: 'Walk order optimized for the fastest route.',
        type: 'success',
        duration: 4000,
      })
    } catch (err) {
      toaster.create({
        title: 'Optimization failed',
        description: extractApiMessage(err, 'An error occurred while optimizing the walk order.'),
        type: 'error',
        duration: 5000,
      })
    } finally {
      setShowOptimizeConfirm(false)
    }
  }

  const handleOptimize = () => {
    if (turf?.orderIsManual) setShowOptimizeConfirm(true)
    else void runOptimize()
  }

  const handleMoveMember = (memberId: string, toPosition: number) => {
    moveMutation.mutate({ memberId, toPosition }, {
      onError: (err) => toaster.create({
        title: 'Move failed',
        description: extractApiMessage(err, 'An error occurred while moving this stop.'),
        type: 'error',
        duration: 6000,
      }),
    })
  }

  const submitMove = async () => {
    if (!moveTarget || !turf) return
    const n = Math.round(Number(movePosition))
    if (!Number.isFinite(n) || n < 1 || n > turf.members.length) return
    try {
      await moveMutation.mutateAsync({ memberId: moveTarget.id, toPosition: n })
      setMoveTarget(null)
    } catch (err) {
      toaster.create({
        title: 'Move failed',
        description: extractApiMessage(err, 'An error occurred while moving this stop.'),
        type: 'error',
        duration: 6000,
      })
    }
  }

  const confirmRemove = async () => {
    if (!removeTarget) return
    try {
      await removeMutation.mutateAsync(removeTarget.id)
      toaster.create({ title: 'Removed from turf', type: 'success', duration: 4000 })
    } catch (err) {
      toaster.create({
        title: 'Remove failed',
        description: extractApiMessage(err, 'An error occurred while removing this stop.'),
        type: 'error',
        duration: 6000,
      })
    } finally {
      setRemoveTarget(null)
    }
  }

  const handleExport = async () => {
    if (!id || !exportLabel.trim()) return
    try {
      const result = await exportMutation.mutateAsync({
        id,
        data: {
          listLabel: exportLabel.trim(),
          listDescription: exportDescription.trim() || undefined,
        },
      })
      toaster.create({
        title: 'Exported to list',
        description: 'Turf members exported as a new contact list.',
        type: 'success',
        duration: 4000,
      })
      exportModal.onClose()
      setExportLabel('')
      setExportDescription('')
      navigate(`/dashboard/lists/${result.listId}/edit`)
    } catch {
      toaster.create({
        title: 'Export failed',
        description: 'An error occurred while exporting the turf to a list.',
        type: 'error',
        duration: 5000,
      })
    }
  }

  const handleDelete = async () => {
    if (!id) return
    try {
      await deleteMutation.mutateAsync(id)
      toaster.create({
        title: 'Turf deleted',
        type: 'success',
        duration: 4000,
      })
      navigate('/dashboard/turfs')
    } catch {
      toaster.create({
        title: 'Delete failed',
        description: 'An error occurred while deleting the turf.',
        type: 'error',
        duration: 5000,
      })
    } finally {
      setShowDeleteDialog(false)
    }
  }

  const handleApplyPlacements = async () => {
    if (!turf) return
    const items = Object.entries(staged)
      .filter(([mid, p]) => p !== turf.members.find(x => x.id === mid)?.locationSource)
      .map(([turfMemberId, locationSource]) => ({ turfMemberId, locationSource }))
    try {
      await setPlacements.mutateAsync(items)
      setStaged({})
      toaster.create({ title: 'Placements saved', type: 'success', duration: 4000 })
    } catch (err) {
      toaster.create({
        title: 'Placement not saved',
        description: extractApiMessage(err, 'An error occurred while saving placements.'),
        type: 'error',
        duration: 6000,
      })
    }
  }

  const handleMarkerClick = (contactId: string) => {
    navigate(`/dashboard/contacts/${contactId}`)
  }

  if (isLoading && !turf) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack py={12}>
          <Spinner size="lg" color="accent" />
          <Text color="gray.500">Loading turf...</Text>
        </VStack>
      </Container>
    )
  }

  if (!!error || !turf) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          Failed to load turf. It may not exist or you may not have access.
        </Alert.Root>
        <Button mt={4} onClick={() => navigate('/dashboard/turfs')}>
          Back to Turfs
        </Button>
      </Container>
    );
  }

  // Derived: the drag list is only reachable (and exitable) on desktop with 2+ stops.
  const reordering = reorderMode && !isMobile && turf.members.length > 1
  const movePositionValid = (() => {
    const n = Number(movePosition)
    return movePosition.trim() !== '' && Number.isInteger(n) && n >= 1 && n <= turf.members.length
  })()

  const progressPercent = turf.members.length > 0
    ? Math.round((turf.members.filter(m => m.isVisited).length / turf.members.length) * 100)
    : 0

  const showPlacement = turf.addressMode === 'Hybrid' && isUnion
  const changedCount = Object.entries(staged)
    .filter(([mid, p]) => p !== turf.members.find(x => x.id === mid)?.locationSource).length

  const placementColumn: ResponsiveColumn<TurfMemberDto> = {
    header: 'Visit at',
    mobileRole: 'meta',
    cell: (m) => {
      const suggested = (useSchedule ? m.scheduleSuggestsWork : m.suggestsWork) ? 'Work' : 'Home'
      return (
        <Stack gap={1} onClick={(e) => e.stopPropagation()}>
          {/* [R10] An added stop keeps its own location; it cannot be re-placed (the server 400s). */}
          {m.opportunisticStopId
            ? <Text fontSize="sm" color="ink.2">Added stop</Text>
            : <PlacementControl name={m.contactName} value={staged[m.id] ?? m.locationSource}
                suggested={suggested} canWork={m.canWork}
                onChange={(p) => setStaged((st) => ({ ...st, [m.id]: p }))} />}
          {m.placementNote && <Text fontSize="xs" color="ink.2">{m.placementNote}</Text>}
        </Stack>
      )
    },
  }

  const memberColumns: ResponsiveColumn<TurfMemberDto>[] = [
    {
      header: '#',
      mobileRole: 'meta',
      isNumeric: true,
      width: '60px',
      cell: (member) => <Text fontWeight="medium" color="ink.2">{member.walkOrder}</Text>,
    },
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (member) => <Text fontWeight="medium">{member.contactName}</Text>,
    },
    ...(showPlacement ? [placementColumn] : []),
    {
      header: 'Address',
      mobileRole: 'meta',
      cell: (member) => (
        <Text fontSize="sm">
          {member.streetAddress}
          {member.city && `, ${member.city}`}
          {member.zip && ` ${member.zip}`}
        </Text>
      ),
    },
    {
      header: 'Visited',
      mobileRole: 'badge',
      width: '100px',
      cell: (member) => (
        member.isVisited
          ? <Badge colorPalette="green">Visited</Badge>
          : <Badge colorPalette="gray">Not Visited</Badge>
      ),
    },
    {
      header: 'Visited At',
      mobileRole: 'desktop-only',
      cell: (member) => (
        <Text fontSize="sm" color="ink.2">
          {member.visitedAt ? new Date(member.visitedAt).toLocaleDateString() : '-'}
        </Text>
      ),
    },
    {
      header: 'Notes',
      mobileRole: 'desktop-only',
      cell: (member) => (
        <Text fontSize="sm" color="ink.2" lineClamp={1}>
          {member.notes ?? '-'}
        </Text>
      ),
    },
    {
      header: 'Actions',
      mobileRole: 'meta',
      width: '64px',
      cell: (member) => (
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <IconButton
              aria-label="Stop actions"
              variant="ghost"
              size="sm"
              minW="44px"
              minH="44px"
              _touch={{ minW: '44px', minH: '44px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <FiMoreVertical />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content onClick={(e) => e.stopPropagation()}>
                <Menu.Item
                  value="move"
                  onSelect={() => { setMoveTarget(member); setMovePosition(String(Math.max(1, turf.members.findIndex(x => x.id === member.id) + 1))) }}
                >
                  <HStack gap={2}><FiArrowDown /><Text>Move to #…</Text></HStack>
                </Menu.Item>
                <Menu.Item value="remove" color="danger.fg" onSelect={() => setRemoveTarget(member)}>
                  <HStack gap={2}><FiTrash2 /><Text>Remove from turf</Text></HStack>
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      ),
    },
  ]

  return (
    <Container maxW="container.xl" py={8} px={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch">
        {/* Back button */}
        {back && (
          <HStack>
            <BackLink to={back.to} label={back.label} />
          </HStack>
        )}

        {/* Header + Actions */}
        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'start' }} gap={4}>
          <Box>
            <Heading size="lg">{turf.label}</Heading>
            {turf.description && (
              <Text color="gray.500" mt={1}>{turf.description}</Text>
            )}
          </Box>
          <Stack direction={{ base: 'column', md: 'row' }} gap={2}>
            <Button size="sm" onClick={handleOptimize} loading={optimizeMutation.isPending}><FiNavigation />Optimize Order
                          </Button>
            <Button
              size="sm"
              onClick={() => {
                setExportLabel(`${turf.label} — Export`)
                exportModal.onOpen()
              }}><FiDownload />Export to List
                          </Button>
            <Button
              size="sm"
              colorPalette="red"
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}><FiTrash2 />Delete
                          </Button>
          </Stack>
        </Stack>

        {/* Metadata */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
            <Box>
              <HStack gap={2} color="gray.500" mb={1}>
                <Icon asChild><FiUsers /></Icon>
                <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">Members</Text>
              </HStack>
              <Text fontWeight="bold" fontSize="lg">{turf.members.length}</Text>
            </Box>
            <Box>
              <HStack gap={2} color="gray.500" mb={1}>
                <Icon asChild><FiCheck /></Icon>
                <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">Progress</Text>
              </HStack>
              <Text fontWeight="bold" fontSize="lg">
                {progressPercent}%
                <Text as="span" fontSize="sm" color="gray.400" ml={1}>
                  ({turf.members.filter(m => m.isVisited).length}/{turf.members.length})
                </Text>
              </Text>
            </Box>
            <Box>
              <HStack gap={2} color="gray.500" mb={1}>
                <Icon asChild><FiClock /></Icon>
                <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">Est. Time</Text>
              </HStack>
              <Text fontWeight="bold" fontSize="lg">
                {turf.estimatedMinutes ? `${turf.estimatedMinutes} min` : '-'}
              </Text>
            </Box>
            <Box>
              <HStack gap={2} color="gray.500" mb={1}>
                <Icon asChild><FiMapPin /></Icon>
                <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">Type</Text>
              </HStack>
              <Text fontWeight="bold" fontSize="lg">{turf.walkType}</Text>
            </Box>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} mt={4} pt={4} borderTop="1px" borderColor="gray.100">
            <Box>
              <Text fontSize="xs" color="gray.500">Source List</Text>
              <Text fontSize="sm">{turf.sourceContactListLabel}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Assigned To</Text>
              <Text fontSize="sm">{turf.assignedToName ?? 'Unassigned'}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Created</Text>
              <Text fontSize="sm">
                {new Date(turf.createdAt).toLocaleDateString()} by {turf.createdByName}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Map */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          {showStops ? (
            <HStack justify="space-between" wrap="wrap" gap={2} mb={3}>
              <Text fontWeight="medium">Map</Text>
              <UnionOnly>
                <HStack gap={3} wrap="wrap">
                <FindNearbyMenu onSearch={runSearch} loading={searching} />
                <Checkbox.Root
                  checked={showPath}
                  onCheckedChange={(e) => setShowPath(!!e.checked)}
                  minH={{ base: '44px', md: 'auto' }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                  <Checkbox.Label>Show drive path</Checkbox.Label>
                </Checkbox.Root>
                </HStack>
              </UnionOnly>
            </HStack>
          ) : (
            <Text fontWeight="medium" mb={3}>Map</Text>
          )}
          <Box mx={{ base: -4, md: 0 }} position={showStops ? 'relative' : undefined}>
            <TurfMap
              boundaries={boundaries}
              contacts={contacts}
              routeGeoJson={showStops ? pathGeoJson : routeGeoJson}
              onMarkerClick={handleMarkerClick}
              height="450px"
              stops={stops}
              showPath={showStops ? showPath : undefined}
              animatePath={showStops || undefined}
              ghosts={ghostFeatures}
              onGhostClick={showStops ? (ghostKey, anchor) => setCard({ ghostKey, anchor }) : undefined}
              onAddInClick={showStops ? (stopId, anchor) => setCard({ stopId, anchor }) : undefined}
            />
            {showStops && (cardGhost || cardStop) && (
              <UnionOnly>
                <GhostCard
                  key={cardGhost?.key ?? cardStop?.id}
                  ghost={cardGhost}
                  stop={cardGhost ? undefined : cardStop}
                  turfId={turf.id}
                  anchor={card?.anchor}
                  onDone={closeCard}
                />
              </UnionOnly>
            )}
          </Box>
          {showStops && (
            <UnionOnly>
              {candidates?.truncated && (
                <Text fontSize="sm" color="fg.muted" mt={2}>Showing the {candidates.shown} nearest</Text>
              )}
              {!!searchError && (
                <Text fontSize="sm" color="danger.fg" mt={2}>
                  {extractApiMessage(searchError, 'Could not search for nearby stops')}
                </Text>
              )}
            </UnionOnly>
          )}
        </Box>

        {/* Member List */}
        <Box bg="surface" borderRadius="lg" shadow="md" overflow="hidden">
          <HStack p={4} borderBottom="1px" borderColor="gray.100" justify="space-between" wrap="wrap" gap={2}>
            <HStack gap={2}>
              <Text fontWeight="medium">
                Members ({turf.members.length})
              </Text>
              {turf.orderIsManual && <Badge colorPalette="brand">Hand-made order</Badge>}
            </HStack>
            {turf.members.length > 1 && (
              <Button
                size="xs"
                variant={reordering ? 'solid' : 'outline'}
                colorPalette="brand"
                display={{ base: 'none', md: 'inline-flex' }}
                onClick={() => setReorderMode(v => !v)}
              >
                Reorder
              </Button>
            )}
          </HStack>
          {showPlacement && (
            <HStack p={4} gap={4} wrap="wrap" borderBottom="1px" borderColor="border">
              <Checkbox.Root checked={useSchedule} onCheckedChange={(e) => setUseSchedule(!!e.checked)}>
                <Checkbox.HiddenInput />
                <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                <Checkbox.Label>Use work schedules to suggest</Checkbox.Label>
              </Checkbox.Root>
              {useSchedule && (
                <Input
                  type="datetime-local"
                  size="sm"
                  w="auto"
                  aria-label="Schedule date and time"
                  value={scheduleAt}
                  onChange={(e) => e.target.value && setScheduleAt(e.target.value)}
                />
              )}
              <Button
                size="sm"
                colorPalette="brand"
                onClick={handleApplyPlacements}
                loading={setPlacements.isPending}
                disabled={changedCount === 0}
              >
                Apply placement ({changedCount})
              </Button>
            </HStack>
          )}
          {turf.members.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text color="gray.500">No members in this turf.</Text>
            </Box>
          ) : reordering ? (
            <TurfMemberReorderList members={turf.members} onMove={handleMoveMember} />
          ) : (
            <ResponsiveTable
              bare
              columns={memberColumns}
              rows={turf.members}
              keyFn={(member) => member.id}
              onRowClick={(member) => navigate(`/dashboard/contacts/${member.contactId}`)}
            />
          )}
        </Box>
      </VStack>

      {/* Delete Dialog */}
      <Dialog.Root
        open={showDeleteDialog}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            setShowDeleteDialog(false);
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Delete Turf
                </Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to delete <strong>"{turf.label}"</strong>? This will remove the
                  turf and all member assignments. This action cannot be undone.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={() => setShowDeleteDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDelete}
                    ml={3}
                    loading={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      {/* Optimize confirm (hand-made order) */}
      <Dialog.Root
        open={showOptimizeConfirm}
        role="alertdialog"
        onOpenChange={e => { if (!e.open) setShowOptimizeConfirm(false) }}>
        <Portal>
          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">Replace your hand-made order?</Dialog.Header>
                <Dialog.Body>
                  Optimizing replaces your hand-made order. The canvasser's phone will optimize the route again.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button variant="ghost" onClick={() => setShowOptimizeConfirm(false)}>Cancel</Button>
                  <Button colorPalette="brand" ml={3} onClick={runOptimize} loading={optimizeMutation.isPending}>
                    Optimize anyway
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>
        </Portal>
      </Dialog.Root>

      {/* Move to # */}
      <ResponsiveModal size="sm" isOpen={!!moveTarget} onClose={() => setMoveTarget(null)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Move {moveTarget?.contactName}</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <Field.Root>
                <Field.Label>New position</Field.Label>
                <Input
                  type="number"
                  size="sm"
                  min={1}
                  max={turf.members.length}
                  value={movePosition}
                  onChange={(e) => setMovePosition(e.target.value)}
                />
                <Field.HelperText>1 to {turf.members.length}</Field.HelperText>
              </Field.Root>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={() => setMoveTarget(null)}>Cancel</Button>
              <Button colorPalette="brand" onClick={submitMove} loading={moveMutation.isPending} disabled={!movePositionValid}>Move</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>

      {/* Remove from turf confirm */}
      <ResponsiveModal size="sm" isOpen={!!removeTarget} onClose={() => setRemoveTarget(null)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Remove from turf</Dialog.Header>
            <Dialog.Body>
              Remove {removeTarget?.contactName} from this turf? Their visit history stays on their contact.
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={() => setRemoveTarget(null)}>Cancel</Button>
              <Button colorPalette="red" onClick={confirmRemove} loading={removeMutation.isPending}>Remove</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>

      {/* Export to List Modal */}
      <ResponsiveModal size="md" isOpen={exportModal.open} onClose={exportModal.onClose}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Export Turf to Contact List</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={4}>
                <Field.Root required>
                  <Field.Label>List Name</Field.Label>
                  <Input
                    size="sm"
                    value={exportLabel}
                    onChange={(e) => setExportLabel(e.target.value)}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Description</Field.Label>
                  <Input
                    size="sm"
                    placeholder="Optional"
                    value={exportDescription}
                    onChange={(e) => setExportDescription(e.target.value)}
                  />
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={exportModal.onClose}>
                Cancel
              </Button>
              <Button
                colorPalette="brand"
                onClick={handleExport}
                loading={exportMutation.isPending}
                disabled={!exportLabel.trim()}
              >
                Export
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </Container>
  );
}
